import { AlertSeverity, setAlert, clearErrors } from './alertActions';

import { AlertTypes } from '../../constants';

import { setSchoolStatus as setServerSchoolStatus, getSchoolStatus, getAlertUpdates, getRoom, getFloor, getBuilding, getEmergencyCallStatus } from '../../api';
import { announceSchoolStatus, announceEmergencyCall, updateEmergencyCallAnnouncement, emergencyCallAnnouncementId, clearAnnouncements } from './announcementActions';

export const IS_STATUS_LOADING = 'IS_STATUS_LOADING';
export const IS_STATUS_NOT_LOADING = 'IS_STATUS_NOT_LOADING';
export const UPDATE_SCHOOL_STATUS = 'UPDATE_SCHOOL_STATUS';
export const UPDATE_ALERT_MODAL = 'UPDATE_ALERT_MODAL';
export const STOP_SHOW_ALERT_ROOM = 'STOP_SHOW_ALERT_ROOM';

export const startLoading = () => ({
  type: IS_STATUS_LOADING
});

export const stopLoading = () => ({
  type: IS_STATUS_NOT_LOADING
});

export const updateSchoolStatus = (roomID = null, status = AlertTypes.UNKNOWN) => ({
  type: UPDATE_SCHOOL_STATUS,
  roomID,
  status
});

export const updateAlertModal = (value) => ({
  type: UPDATE_ALERT_MODAL,
  value
});

export const stopShowAlertRoom = () => ({
  type: STOP_SHOW_ALERT_ROOM
});

const ANNOUNCED_CALL_KEY = 'statusActions.announcedEmergencyCall';

/**
 * Forgets which emergency call has already been announced, so the next session
 * re-announces a dispatch that is still failing rather than staying silent
 * about it.
 */
export const clearAnnouncedEmergencyCall = () => {
  try {
    sessionStorage.removeItem(ANNOUNCED_CALL_KEY);
  }
  catch (e) {
    // Storage can be unavailable; nothing here is worth failing a logout over.
  }
};

/**
 * Tracks the most recent emergency call and keeps a banner in step with it,
 * from dispatch through to whatever it resolves as.
 *
 * A first sighting announces (and scrolls into view); later status changes on
 * the same call update the existing bar in place. An unchanged status does
 * nothing at all, which matters because this runs on the same fast poll as the
 * school status.
 */
export const checkEmergencyCallStatus = (dispatch) => {
  getEmergencyCallStatus().then(call => {
    if (call == null) return;

    const announcementId = emergencyCallAnnouncementId(call);

    let seen;
    try {
      seen = JSON.parse(sessionStorage.getItem(ANNOUNCED_CALL_KEY) ?? 'null');
    }
    catch (e) {
      seen = null;
    }

    // Same call, same status: nothing has moved since the last poll.
    if (seen?.id === announcementId && seen?.status === call.status) return;

    sessionStorage.setItem(ANNOUNCED_CALL_KEY, JSON.stringify({ id: announcementId, status: call.status }));

    if (seen?.id === announcementId) {
      updateEmergencyCallAnnouncement(dispatch, call);
    }
    else {
      announceEmergencyCall(dispatch, call);
    }
  }).catch(() => {
    // Polling failure is already surfaced by the school status check; staying
    // quiet here avoids a second banner for the same underlying outage.
  });
};

export const checkSchoolStatus = (dispatch, isAuthenticated = false) => {
  dispatch(startLoading());

  // Only while logged in. This poll runs on every page including the login
  // screen, and the emergency call status endpoint requires authentication, so
  // polling it logged out returns 401. The API client force-logs-out and
  // reloads the page on any 401, and the reload re-runs this poll immediately,
  // which traps the login screen in a reload loop. The other calls below are
  // deliberately unauthenticated or tolerate a missing session.
  if (isAuthenticated) checkEmergencyCallStatus(dispatch);

  getSchoolStatus().then(data => {
    dispatch(stopLoading());
    if (data) dispatch(updateSchoolStatus(data.roomID, data.status));
  }).catch(() => {
    dispatch(stopLoading());
    dispatch(updateSchoolStatus());
  });

  getAlertUpdates(3).then(alertUpdates => {
    let seenAlertUpdateIds;
    try {
      seenAlertUpdateIds = JSON.parse(sessionStorage.getItem('statusActions.seenAlertUpdateIds') ?? '[]');
    }
    catch (e) {
      seenAlertUpdateIds = [];
    }

    if (alertUpdates) {
      for (const alert of alertUpdates) {
        if (alert != null) {
          // Make sure we haven't already seen the alert
          if (seenAlertUpdateIds.includes(alert.id)) continue;

          // Update the list of seen alert update ids
          seenAlertUpdateIds.push(alert.id);
          sessionStorage.setItem('statusActions.seenAlertUpdateIds', JSON.stringify(seenAlertUpdateIds));
          
          const alertType = Object.values(AlertTypes).find(alertType => alertType.getID() === alert.getAlertTypeID());

          // If the alert type is not found, skip it
          if (alertType == null) {
            console.log('Alert type not found for alert type id ' + alert.getAlertTypeID());
            continue;
          }

          // Don't show alert banner for normal or all clear alerts
          if (alertType.getDashboardStatus() == 'Normal' || alertType.getDashboardStatus() == 'All Clear') continue;

          getRoom(alert.getRoomID()).then(room => {
            getFloor(room.getFloorID()).then(floor => {
              getBuilding(floor.getBuildingID()).then(building => {
                announceSchoolStatus(dispatch, {
                  status: alertType,
                  room,
                  floor,
                  building
                });
              });
            });
          });
        }
      }
    }
  });
};

export const setSchoolStatus = (dispatch, alertStatus) => {
  dispatch(startLoading());
  dispatch(clearAnnouncements());
  setServerSchoolStatus(alertStatus).then(() => {
    dispatch(stopLoading());
    dispatch(updateSchoolStatus(null, alertStatus));
    dispatch(clearErrors());
    setAlert(dispatch, 'Successfully set school status to ' + alertStatus.getDashboardStatus(), AlertSeverity.SUCCESS, true);
  }).catch(() => {
    dispatch(stopLoading());
    setAlert(dispatch, 'Unable to set school status.');
  });
};

export const clearSchoolStatus = (dispatch) => {
  setSchoolStatus(dispatch, AlertTypes.NORMAL);
};

export const hideAlertModal = (dispatch) => {
  dispatch(updateAlertModal(false));
};
